import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {AsyncPipe, NgIf} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {first, tap} from 'rxjs/operators';
import {IconButtonComponent, ToolbarModule} from '@q9elements/ui-kit/components';
import {
  PermissionsService,
  Q9TransformPipe,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {GraphHelperService} from '../../../diagram-render/helpers/graph-helper.service';
import {MapService} from '../../../core/map/map.service';
import {ActivityCommands} from '../../../commands/activity/activity.commands';
import {TextCommands} from '../../../commands/text/text.commands';
import {FlowlineCommands} from '../../../commands/flowline/flowline.commands';
import {StickerCommands} from '../../../commands/sticky-note/sticker.commands';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {ImageUploadService} from '../../../core/services/image.upload.service';
import {ModeDataService} from '../../../editor-state/mode-data.service';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';
import {ActivityToolbarComponent} from '../activity-toolbar/activity-toolbar.component';
import {AgentforceGenerateActionsComponent} from './components/diagram-ai-features/agentforce-generate-actions.component';
import {DiagramJSON} from '../../../shared/models/diagram-json.interface';
import {PopupManagerService} from '../../../core/services/popup-manager.service';
import {DragHandlerService} from '../shared/drag-handler.service';
import {StylesService} from '../../../diagram-render/styles/styles.service';
import {UndoService} from '../../../commands/undo.service';
import {
  PositionType,
  CanvasElementType
} from '../../../diagram-render/styles/diagram-type-styles.model';
import {StickerNoteToolbarComponent} from '../sticker-note-toolbar/sticker-note-toolbar.component';

interface PreviewElement {
  previewElement: HTMLElement;
  offset: {dx: number; dy: number};
}

type MxDropEventCallback = (graph, event, target, x: number, y: number) => void;

declare const mxUtils: any;

enum BUTTON_TYPES {
  ACTIVITY = 'ACTIVITY',
  TOPIC = 'TOPIC',
  TEXT = 'TEXT',
  GUARDRAIL = 'GUARDRAIL',
  FLOWLINE = 'FLOWLINE',
  STICKER = 'STICKER',
  IMAGE = 'IMAGE',
  RENUMBERING = 'RENUMBERING'
}

const TOOLTIPS = {
  [BUTTON_TYPES.ACTIVITY]: 'ASIDE_L.AGENT_ACTIVITY_TOOLTIP',
  [BUTTON_TYPES.TEXT]: 'ASIDE_L.TEXT_TOOLTIP',
  [BUTTON_TYPES.GUARDRAIL]: 'ASIDE_L.GUARDRAIL_TOOLTIP',
  [BUTTON_TYPES.FLOWLINE]: 'ASIDE_L.FLOWLINE_TOOLTIP',
  [BUTTON_TYPES.STICKER]: 'ASIDE_L.STICKER_TOOLTIP_NEW',
  [BUTTON_TYPES.IMAGE]: 'ASIDE_L.IMAGE_TOOLTIP',
  [BUTTON_TYPES.RENUMBERING]: 'ASIDE_L.RENUMBERING_TOOLTIP'
} as const;

@Component({
  selector: 'app-left-toolbar',
  templateUrl: './left-toolbar.component.html',
  styleUrls: ['./left-toolbar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    NgIf,
    ToolbarModule,
    IconButtonComponent,
    MatTooltipModule,
    TranslateModule,
    Q9TransformPipe,
    AgentforceGenerateActionsComponent,
    ActivityToolbarComponent,
    StickerNoteToolbarComponent
  ]
})
export class LeftToolbarComponent extends TakeUntilComponentDestroy implements AfterViewInit {
  @ViewChild('activityDragElement', {read: ElementRef}) private activityDragElement;
  @ViewChild('textDragElement', {read: ElementRef}) private textDragElement;
  @ViewChild('flowlineDragElement', {read: ElementRef}) private flowlineDragElement;
  @ViewChild('stickerDragElement', {read: ElementRef}) private stickerDragElement;
  @ViewChild('imageDragElement', {read: ElementRef}) private imageDragElement;

  public diagramJson$: Observable<DiagramJSON> = this.diagramJsonService.diagramJson$;
  public readonly isViewMode = toSignal(this.modeDataSvc.isViewMode$);
  public readonly isAgentInstruction = this.diagramTypeConfigSvc.isAgentInstruction;
  public showAgentforce = computed(() => {
    return (
      this.diagramTypeConfigSvc.isAgentInstruction() &&
      this.permissionsService.hasAgentDiagramsLicense()
    );
  });
  public showActivityToolbar = signal(false);
  public showStickerToolbar = signal(false);

  public readonly isActivityDisabled = computed(() => {
    return this.diagramTypeConfigSvc
      .generalSettings()
      .disabledCanvasElements?.includes(CanvasElementType.ACTIVITY);
  });
  public readonly isFlowlineDisabled = computed(() => {
    return this.diagramTypeConfigSvc
      .generalSettings()
      .disabledCanvasElements?.includes(CanvasElementType.FLOWLINE);
  });
  public readonly isTextDisabled = computed(() => {
    return this.diagramTypeConfigSvc
      .generalSettings()
      .disabledCanvasElements?.includes(CanvasElementType.TEXT);
  });
  public readonly isStickerNoteDisabled = computed(() => {
    return this.diagramTypeConfigSvc
      .generalSettings()
      .disabledCanvasElements?.includes(CanvasElementType.STICKY_NOTE);
  });
  public readonly isImageDisabled = computed(() => {
    return this.diagramTypeConfigSvc
      .generalSettings()
      .disabledCanvasElements?.includes(CanvasElementType.IMAGE);
  });

  public readonly BUTTON_TYPES = BUTTON_TYPES;
  public readonly TOOLTIPS = TOOLTIPS;

  constructor(
    private readonly graphService: GraphHelperService,
    private readonly mapService: MapService,
    private readonly diagramJsonService: DiagramJsonHandlerService,
    private readonly imageUploadService: ImageUploadService,
    private readonly activityCommands: ActivityCommands,
    private readonly textCommands: TextCommands,
    private readonly popupSvc: PopupManagerService,
    private readonly flowlineCommands: FlowlineCommands,
    private readonly stickerCommands: StickerCommands,
    private readonly modeDataSvc: ModeDataService,
    private readonly elementRef: ElementRef,
    private diagramTypeConfigSvc: DiagramTypeConfigService,
    private dragHandlerSvc: DragHandlerService,
    private stylesSvc: StylesService,
    private readonly undoService: UndoService,
    private readonly permissionsService: PermissionsService
  ) {
    super();
  }

  ngAfterViewInit() {
    this.mapService.map$
      .pipe(
        first(map => Boolean(map?.id)),
        tap(() => {
          this.setupHandlers();
        })
      )
      .subscribe(() => mxUtils.writeElementBounds('LEFT_TOOLBAR_BOUNDS', this.elementRef));

    this.modeDataSvc.isViewMode$
      .pipe(
        this.takeUntilComponentDestroy(),
        tap(() => {
          this.showActivityToolbar.set(false);
          this.showStickerToolbar.set(false);
        })
      )
      .subscribe();
  }

  private setupHandlers() {
    this.setupTextDragHandler();
    this.setupFlowlineDragHandler();
    this.setupImageDragHandler();
  }

  private setupTextDragHandler() {
    if (this.isTextDisabled()) {
      return;
    }

    const createCommand = (x: number, y: number) => {
      const primaryStyle = this.stylesSvc.getPrimary('text');
      const textData = this.diagramTypeConfigSvc.richTextSettings.preparePayload({
        x,
        y,
        number: this.graphService.getFreeNumber(4),
        style: primaryStyle,
        position: PositionType.DEFAULT
      });

      return this.textCommands.create(textData);
    };

    const {w: width, h: height} = this.diagramTypeConfigSvc.richTextSettings.defaultTextShape;
    const previewElement = this.dragHandlerSvc.createPreviewElement(width, height);

    this.dragHandlerSvc.createDragHandler(
      createCommand,
      this.textDragElement.nativeElement,
      previewElement
    );
  }

  private setupFlowlineDragHandler() {
    if (this.isFlowlineDisabled()) {
      return;
    }

    const createCommand = (x: number, y: number) => {
      const {
        config: {text, position},
        defaultFlowlineShape
      } = this.diagramTypeConfigSvc.flowlineSettings(x, y);

      const flowlineData = {
        text,
        srcType: 'none',
        srcId: null,
        tgtType: 'none',
        tgtId: null,
        position: {
          x: position.x ?? 0,
          y: position.y ?? 15,
          offset: {x: 0, y: 0},
          connectionPoints: {}
        },
        srcPosition: {x, y},
        tgtPosition: {x: x + defaultFlowlineShape.w, y},
        waypoints: null
      };

      return this.flowlineCommands.create(flowlineData);
    };

    const {w: width, h: height} = this.diagramTypeConfigSvc.flowlineSettings().defaultFlowlineShape;
    const previewElement = this.dragHandlerSvc.createPreviewElement(width, height);

    this.dragHandlerSvc.createDragHandler(
      createCommand,
      this.flowlineDragElement.nativeElement,
      previewElement
    );
  }

  private setupStickerDragHandler() {
    if (this.isStickerNoteDisabled()) {
      return;
    }

    const createCommand = (x: number, y: number) => {
      return this.stickerCommands.create(
        this.diagramTypeConfigSvc.stickerNoteSettings.preparePayload({
          x,
          y,
          mxKey: this.graphService.getFreeNumber(7),
          position: PositionType.DEFAULT
        })
      );
    };

    const {w: width, h: heigh} = this.diagramTypeConfigSvc.stickerNoteSettings.defaultShape;

    const previewElement = this.dragHandlerSvc.createPreviewElement(width, heigh);

    this.dragHandlerSvc.createDragHandler(
      createCommand,
      this.stickerDragElement.nativeElement,
      previewElement,
      true
    );
  }

  private setupImageDragHandler() {
    if (this.isImageDisabled()) {
      return;
    }

    const createCommand = (x: number, y: number, event) => {
      this.imageUploadService.createImage(event);
    };

    const previewElement = this.dragHandlerSvc.createPreviewElement(300, 300, -150, -150);

    this.dragHandlerSvc.createDragHandler(
      createCommand,
      this.imageDragElement.nativeElement,
      previewElement
    );
  }

  public toggleActivityToolbar() {
    this.showActivityToolbar.update(value => {
      const next = !value;

      if (next) {
        this.showStickerToolbar.set(false);
      }

      return next;
    });
  }

  public toggleStickerNoteToolbar() {
    this.showStickerToolbar.update(value => {
      const next = !value;

      if (next) {
        this.showActivityToolbar.set(false);
      }

      return next;
    });
  }

  public getTooltipText(buttonType: BUTTON_TYPES): string {
    if (this.isViewMode()) {
      return 'ASIDE_L.EDIT_MODE_REQUIRED_TOOLTIP';
    }

    return TOOLTIPS[buttonType];
  }

  public renumber(): void {
    this.popupSvc.openActivityReorderDialog('header-renumber');
  }
}
